import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import RelatedServices from '../../../components/feature/RelatedServices';
import RelatedIndustries from '../../../components/feature/RelatedIndustries';
import { useSEO } from '../../../utils/seo';

export default function MedicalSEOPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Medical SEO Services | Healthcare SEO Agency | Rank #1 for Patient Searches',
    description: 'Professional medical SEO services for healthcare providers. Dominate local search results, attract qualified patients, and grow your practice with HIPAA-compliant SEO strategies. Get found by patients actively searching for your services.',
    keywords: 'medical SEO, healthcare SEO, local SEO for doctors, medical practice SEO, healthcare search optimization, patient acquisition SEO',
    canonical: `${siteUrl}/services/medical-seo`,
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Medical SEO Services",
      "description": "Professional SEO services for healthcare providers to dominate local search results and attract qualified patients",
      "provider": {
        "@type": "Organization",
        "name": "MillionDollarMed",
        "url": siteUrl
      },
      "areaServed": "US",
      "serviceType": "Medical SEO"
    }
  });

  const features = [
    'Local SEO Optimization',
    'Google Business Profile Management',
    'Healthcare Keyword Research',
    'On-Page SEO Optimization',
    'Technical SEO Audits',
    'Medical Content Strategy',
    'Local Citation Building',
    'Review Generation & Management',
    'Competitor Analysis',
    'Schema Markup Implementation',
    'Mobile SEO Optimization',
    'Monthly Performance Reporting'
  ];

  const process = [
    {
      step: '01',
      title: 'SEO Audit & Analysis',
      description: 'Comprehensive analysis of your current rankings, website health, competitors, and local search opportunities.'
    },
    {
      step: '02',
      title: 'Strategy Development',
      description: 'Custom SEO roadmap targeting high-intent healthcare keywords and local search terms your patients are using.'
    },
    {
      step: '03',
      title: 'On-Page Optimization',
      description: 'Optimize every page with proper titles, meta descriptions, headers, and healthcare-specific schema markup.'
    },
    {
      step: '04',
      title: 'Local SEO & Citations',
      description: 'Optimize Google Business Profile, build local citations, and dominate map pack results in your area.'
    },
    {
      step: '05',
      title: 'Content & Link Building',
      description: 'Create authority-building medical content and earn high-quality backlinks from healthcare sources.'
    },
    {
      step: '06',
      title: 'Monitoring & Reporting',
      description: 'Track rankings, traffic, and patient conversions with detailed monthly reports and ongoing optimization.'
    }
  ];

  const benefits = [
    {
      icon: 'ri-map-pin-line',
      title: 'Dominate Local Search',
      description: 'Appear at the top of Google when patients in your area search for your services and specialties.'
    },
    {
      icon: 'ri-user-search-line',
      title: 'Attract Qualified Patients',
      description: 'Target high-intent keywords that bring patients actively looking for your specific healthcare services.'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Sustainable Growth',
      description: 'SEO delivers long-term results that compound over time, unlike paid ads that stop when you stop paying.'
    },
    {
      icon: 'ri-trophy-line',
      title: 'Beat Your Competitors',
      description: 'Outrank competing practices and capture patients who would otherwise choose your competition.'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Lower Cost Per Patient',
      description: 'Organic search traffic has a much lower cost per acquisition than paid advertising over time.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'HIPAA-Compliant Strategy',
      description: 'All SEO tactics follow healthcare marketing guidelines and maintain patient privacy standards.'
    }
  ];

  const stats = [
    { number: '75%', label: 'of patients never scroll past the first page of search results' },
    { number: '46%', label: 'of all Google searches are looking for local information' },
    { number: '3x', label: 'more traffic from organic search than social media' },
    { number: '93%', label: 'of online experiences begin with a search engine' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                MEDICAL SEO &amp; LOCAL SEO
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Rank #1 When Patients Search for Your Services
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Dominate local search results and attract qualified patients actively searching for healthcare providers in your area. Our healthcare-specific SEO strategies get you found by the right patients at the right time.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="/book-strategy-call"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#7C3AED] to-[#A855F7] text-white px-8 py-4 rounded-lg hover:shadow-lg transition-all cursor-pointer whitespace-nowrap font-bold"
                >
                  <span>Get Your Free SEO Audit</span>
                  <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center gap-2 bg-white text-[#7C3AED] border-2 border-[#7C3AED] px-8 py-4 rounded-lg hover:bg-[#7C3AED]/5 transition-all cursor-pointer whitespace-nowrap font-medium"
                >
                  <span>See Our Process</span>
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/b8a0adeb6ac41697f7fe811016415ec7.jpg"
                  alt="Medical SEO rankings"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 lg:px-12 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#7C3AED] mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              WHY MEDICAL SEO MATTERS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Get Found by Patients Actively Looking for You
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              When patients search for healthcare services, they choose from the top results. Our medical SEO strategies ensure your practice appears first when it matters most.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-lg flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Medical%20SEO%20dashboard%20showing%20keyword%20rankings%20local%20search%20performance%20Google%20Business%20Profile%20optimization%20and%20healthcare%20website%20analytics%20with%20rising%20traffic%20graphs%20professional%20digital%20marketing%20interface%20purple%20accents%20clean%20modern%20design&width=600&height=700&seq=seofeatures1&orientation=portrait"
                  alt="SEO features dashboard"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
                COMPREHENSIVE SEO SERVICES
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
                Everything You Need to Dominate Search Results
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-8">
                From technical optimization to local citations and content strategy, we handle every aspect of your medical practice SEO.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#7C3AED]/10 rounded-full flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-check-line text-[#7C3AED] text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="text-xs uppercase tracking-[0.2em] text-[#7C3AED] font-bold mb-4">
              OUR SEO PROCESS
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Proven Strategy That Delivers Results
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              Our systematic approach to medical SEO ensures consistent improvement in rankings, traffic, and patient acquisition month after month.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div
                key={index}
                className="bg-[#F9FAFB] rounded-xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#7C3AED] to-[#A855F7] rounded-xl flex items-center justify-center mb-6">
                  <span className="text-2xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-[#0D9488] to-[#14B8A6] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/10"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
            Ready to Dominate Local Search Results?
          </h2>
          <p className="text-lg text-white/90 mb-8 leading-relaxed">
            Get a free SEO audit and custom strategy showing exactly how we'll get your practice ranking #1 for your most valuable keywords.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href="/book-strategy-call"
              className="inline-flex items-center gap-2 bg-white text-[#0D9488] px-8 py-4 rounded-lg hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap font-bold"
            >
              <span>Get Your Free SEO Audit</span>
              <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap font-medium"
            >
              <span>See Case Studies</span>
            </a>
          </div>
        </div>
      </section>

      <RelatedServices currentSlug="medical-seo" />
      <RelatedIndustries />

      <Footer />
    </div>
  );
}
